// labEngine.js — pure functions, no React. Same file can run in the browser and on a Node server.
// Every calc returns { result, issues }. issue = { level: 'block' | 'warn', code, clause, msg }.
// 'block' = report must not be issued until fixed. Nothing here fills in or "corrects" raw readings.

const issue = (level, code, clause, msg) => ({ level, code, clause, msg });
const num = (v) => typeof v === 'number' && Number.isFinite(v);

// IS 2 rounding (half up) to a step, then trim float noise
export const roundStep = (x, step, nd = 3) => Number((Math.floor(x / step + 0.5 + 1e-9) * step).toFixed(nd));
export const sig2 = (x) => (x === 0 ? 0 : Number(x.toPrecision(2)));
const moisture = (wet, dry, tare) => ((wet - dry) / (dry - tare)) * 100;

// Reject readings finer than the balance can give (catches back-calculated values like 10907.397625)
export function checkResolution(label, value, step, clause) {
  if (!num(value)) return issue('block', 'MISSING', clause, `${label}: value missing`);
  const k = value / step;
  return Math.abs(k - Math.round(k)) > 1e-6
    ? issue('block', 'RESOLUTION', clause, `${label}: ${value} is finer than the balance resolution (${step} g)`)
    : null;
}
const collect = (...xs) => xs.flat().filter(Boolean);

function polyfit(xs, ys, deg) {
  const m = deg + 1;
  const A = Array.from({ length: m }, (_, r) =>
    Array.from({ length: m + 1 }, (_, c) =>
      c < m ? xs.reduce((s, x) => s + x ** (r + c), 0) : xs.reduce((s, x, i) => s + ys[i] * x ** r, 0)));
  for (let i = 0; i < m; i++) {
    let p = i;
    for (let r = i + 1; r < m; r++) if (Math.abs(A[r][i]) > Math.abs(A[p][i])) p = r;
    [A[i], A[p]] = [A[p], A[i]];
    for (let r = i + 1; r < m; r++) {
      const f = A[r][i] / A[i][i];
      for (let c = i; c <= m; c++) A[r][c] -= f * A[i][c];
    }
  }
  const c = Array(m).fill(0);
  for (let i = m - 1; i >= 0; i--) {
    let v = A[i][m];
    for (let j = i + 1; j < m; j++) v -= A[i][j] * c[j];
    c[i] = v / A[i][i];
  }
  return c; // c[0] + c[1] x + c[2] x^2
}
const rSquared = (xs, ys, pred) => {
  const mean = ys.reduce((a, b) => a + b, 0) / ys.length;
  const ssTot = ys.reduce((s, y) => s + (y - mean) ** 2, 0);
  const ssRes = ys.reduce((s, y, i) => s + (y - pred(xs[i])) ** 2, 0);
  return 1 - ssRes / ssTot;
};

// ---------- Part 2: minimum dry specimen for water content (g), by max particle size (mm) ----------
const P2_MIN_G = [[0.425, 25], [2, 50], [4.75, 200], [10, 300], [20, 500], [40, 1000]];
const p2Min = (maxMm) => (P2_MIN_G.find(([s]) => maxMm <= s) || [0, 1000])[1];

// ---------- IS 2720 Part 4:1985 — grain size ----------
// Stage 1: whole sample on 100/75/19/4.75 mm.  Stage 2: riffled fraction passing 4.75 on 2 mm/425 µ/75 µ.
export const COARSE = [100, 75, 19, 4.75];
export const FINE = [2, 0.425, 0.075];
const FINE_LOAD_LIMIT_G = [200, 50, 25]; // Part 4 Note 3 to 4.3.1.1 (200 mm sieves)

export function gsa({ totalDryG, coarseRetainedG, partial, hasClay = false }) {
  // partial = { massG, retainedG:[2mm,425µ,75µ], method:'wet'|'dry', panG? }
  const clause = 'IS 2720-4:1985';
  const issues = [];
  let cum = 0;
  const rows = COARSE.map((size, i) => {
    cum += coarseRetainedG[i];
    return { size, retainedG: coarseRetainedG[i], pctPassing: 100 - (cum / totalDryG) * 100 };
  });
  const pass475 = rows[3].pctPassing;
  let fc = 0;
  const fineRows = FINE.map((size, i) => {
    fc += partial.retainedG[i];
    const passPartial = 100 - (fc / partial.massG) * 100;
    return { size, retainedG: partial.retainedG[i], pctPassing: (passPartial * pass475) / 100 };
  });
  if (partial.method === 'dry' && hasClay)
    issues.push(issue('block', 'DRY_ON_CLAY', `${clause} 4.3.2.0`, 'Dry sieving not allowed for clayey soil — use wet sieving'));
  partial.retainedG.forEach((w, i) => {
    if (w > FINE_LOAD_LIMIT_G[i])
      issues.push(issue('warn', 'SIEVE_OVERLOAD', `${clause} Note 3`, `${FINE[i]} mm sieve holds ${w} g (max ${FINE_LOAD_LIMIT_G[i]} g per batch) — record batches`));
  });
  if (partial.method === 'dry') {
    if (!num(partial.panG)) issues.push(issue('block', 'NO_PAN', clause, 'Pan mass required for dry sieving mass check'));
    else {
      const err = Math.abs(partial.massG - (fc + partial.panG)) / partial.massG * 100;
      if (err > 0.1) issues.push(issue('block', 'MASS_BALANCE', `${clause} 3.1.1`, `Retained + pan differs from sample by ${err.toFixed(2)}% (limit 0.1%)`));
    }
  }
  const all = [...rows, ...fineRows];
  const p = (s) => all.find((r) => r.size === s).pctPassing;
  const result = {
    rows: all,
    gravelPct: 100 - p(4.75),
    coarseSandPct: p(4.75) - p(2),
    mediumSandPct: p(2) - p(0.425),
    fineSandPct: p(0.425) - p(0.075),
    finesPct: p(0.075),
  };
  result.sandPct = result.coarseSandPct + result.mediumSandPct + result.fineSandPct;
  if (result.finesPct >= 10) issues.push(issue('warn', 'SEDIMENTATION', `${clause} 1.1.2`, 'Fines ≥ 10%: pipette/hydrometer applies if silt/clay split is required'));
  return { result, issues };
}

// ---------- IS 2720 Part 5:1985 — Atterberg limits ----------
export function llpl({ ll, pl, soilHistory, soakHours, plNotObtainable = false }) {
  // ll = [{ blows, wetG, dryG, tareG }] ; pl = [{ wetG, dryG, tareG }]
  const clause = 'IS 2720-5:1985';
  const issues = [];
  const llPts = ll.map((r) => ({ N: r.blows, w: moisture(r.wetG, r.dryG, r.tareG) }));
  const plPts = pl.map((r) => moisture(r.wetG, r.dryG, r.tareG));
  ll.forEach((r, i) => [['wetG'], ['dryG'], ['tareG']].forEach(([k]) =>
    issues.push(checkResolution(`LL #${i + 1} ${k}`, r[k], 0.01, `${clause} 3.1.7`))));
  pl.forEach((r, i) => ['wetG', 'dryG', 'tareG'].forEach((k) =>
    issues.push(checkResolution(`PL #${i + 1} ${k}`, r[k], 0.01, `${clause} 7.1.5`))));
  if (ll.length < 4) issues.push(issue('block', 'LL_POINTS', `${clause} 3.4.5`, `Need at least 4 liquid-limit trials (have ${ll.length})`));
  llPts.forEach((p, i) => { if (p.N < 15 || p.N > 35) issues.push(issue('block', 'BLOWS_RANGE', `${clause} 3.4.5`, `LL trial ${i + 1}: ${p.N} blows outside 15–35`)); });
  if (pl.length < 3 && !plNotObtainable) issues.push(issue('block', 'PL_POINTS', `${clause} 7.4.2`, `Need at least 3 plastic-limit determinations (have ${pl.length})`));
  if (!soilHistory) issues.push(issue('block', 'HISTORY', `${clause} 3.6.2 / 7.4.3`, 'Soil history (natural / air-dried / oven-dried) not reported'));
  if (!num(soakHours)) issues.push(issue('block', 'SOAK', `${clause} 3.6.2`, 'Soaking period after mixing not reported'));

  let result = null;
  if (llPts.length >= 2) {
    const xs = llPts.map((p) => Math.log10(p.N));
    const ys = llPts.map((p) => p.w);
    const [a, b] = polyfit(xs, ys, 1);
    const r2 = rSquared(xs, ys, (x) => a + b * x);
    if (r2 < 0.98) issues.push(issue('warn', 'FLOW_CURVE_FIT', `${clause} 3.5.1`, `Flow-curve R² = ${r2.toFixed(3)} — points scatter`));
    const llRaw = a + b * Math.log10(25);
    const LL = roundStep(llRaw, 1, 0);
    let PL = null, PI = null;
    if (plPts.length && !plNotObtainable) {
      PL = roundStep(plPts.reduce((s, x) => s + x, 0) / plPts.length, 1, 0);
      PI = PL >= LL ? 0 : LL - PL; // 8.2(b)
    } else if (plNotObtainable) PI = 'NP'; // 8.2(a)
    result = { LL, PL, PI, llRaw, flowIndex: -b, r2, llPoints: llPts, plPoints: plPts };
  }
  return { result, issues: collect(issues) };
}

// ---------- IS 2720 Part 8:1983 — heavy compaction ----------
export function mdd({ mould, rammerKg, layers, blowsPerLayer, retained19Pct, procedure, maxSizeMm = 19, trials }) {
  const clause = 'IS 2720-8:1983';
  const issues = [];
  if (rammerKg !== 4.9) issues.push(issue('block', 'RAMMER', `${clause} 3.9`, 'Heavy compaction needs the 4.9 kg rammer'));
  const expBlows = mould.volumeCc === 2250 ? 55 : mould.volumeCc === 1000 ? 25 : null;
  if (expBlows && (blowsPerLayer !== expBlows || layers !== 5))
    issues.push(issue('block', 'BLOWS', `${clause} 5.1.2 / 5.3`, `${mould.volumeCc} cc mould: 5 layers × ${expBlows} blows`));
  if (!expBlows) issues.push(issue('block', 'MOULD', `${clause} 3.1`, 'Mould must be 1000 cc or 2250 cc'));
  if (!num(retained19Pct)) issues.push(issue('block', 'RETAINED_19', `${clause} 7.4`, '% retained on 19 mm sieve must be reported'));
  if (!procedure) issues.push(issue('block', 'PROCEDURE', `${clause} 7.5`, 'State single-sample or separate-sample procedure'));
  if (trials.length < 5) issues.push(issue('block', 'POINTS', `${clause} 5.1.4`, `At least 5 determinations required (have ${trials.length})`));
  issues.push(checkResolution('Mould mass', mould.massG, 1, `${clause} 5.1.2`));

  const pts = trials.map((t, i) => {
    issues.push(checkResolution(`Trial ${i + 1} mould+soil`, t.mouldSoilG, 1, `${clause} 5.1.2`));
    ['wetG', 'dryG', 'tareG'].forEach((k) => issues.push(checkResolution(`Trial ${i + 1} ${k}`, t[k], 0.01, 'IS 2720-2:1973 3.2')));
    const bulk = (t.mouldSoilG - mould.massG) / mould.volumeCc;
    const w = moisture(t.wetG, t.dryG, t.tareG);
    const dryMass = t.dryG - t.tareG;
    if (dryMass < p2Min(maxSizeMm))
      issues.push(issue('warn', 'W_SPECIMEN', 'IS 2720-2:1973 4.1', `Trial ${i + 1}: moisture specimen ${dryMass.toFixed(1)} g < ${p2Min(maxSizeMm)} g minimum`));
    return { w, bulk, dry: (100 * bulk) / (100 + w), dryMass };
  });
  const dupes = pts.map((p) => p.dryMass.toFixed(2));
  dupes.forEach((d, i) => { if (dupes.indexOf(d) !== i) issues.push(issue('warn', 'DUPLICATE', 'data integrity', `Trials ${dupes.indexOf(d) + 1} and ${i + 1} have identical dry soil mass (${d} g) — recheck`)); });

  let result = null;
  if (pts.length >= 3) {
    const pk = pts.reduce((m, p, i) => (p.dry > pts[m].dry ? i : m), 0);
    if (pk === 0 || pk === pts.length - 1) issues.push(issue('block', 'NOT_BRACKETED', `${clause} 5.1.4`, 'Peak at first/last point — optimum not bracketed'));
    const lo = Math.max(0, pk - 2), hi = Math.min(pts.length, pk + 3);
    const sel = pts.slice(lo, hi);
    const c = polyfit(sel.map((p) => p.w), sel.map((p) => p.dry), 2);
    const omcRaw = -c[1] / (2 * c[2]);
    const mddRaw = c[0] + c[1] * omcRaw + c[2] * omcRaw ** 2;
    const omcStep = omcRaw < 5 ? 0.2 : omcRaw <= 10 ? 0.5 : 1; // 7.3
    result = {
      points: pts, mddRaw, omcRaw, coef: c,            // curve-fit suggestion; technician confirms from the plotted curve (coef = a + b·w + c·w²)
      mdd: roundStep(mddRaw, 0.01, 2),                 // 7.2
      omc: roundStep(omcRaw, omcStep, 1),              // 7.3
      retained19Pct: num(retained19Pct) ? roundStep(retained19Pct, 1, 0) : null, // 7.4
    };
  }
  return { result, issues: collect(issues) };
}

// ---------- IS 2720 Part 40:1977 — free swell index ----------
export function fsi({ specimens, settleHours }) {
  // specimens = [{ vdCc, vkCc }]
  const issues = [];
  if (!(settleHours >= 24)) issues.push(issue('block', 'SETTLE', 'IS 2720-40:1977 3.2', 'Settling time must be at least 24 h'));
  const vals = specimens.map((s) => ((s.vdCc - s.vkCc) / s.vkCc) * 100);
  return { result: { values: vals, fsiPct: vals.reduce((a, b) => a + b, 0) / vals.length }, issues };
}

// ---------- IS 2720 Part 16:1987 — CBR ----------
export function cbrMould({ ringFactor, areaCm2 = 19.64, readings, originShiftMm = 0 }) {
  // readings = [{ pen, dial }]. originShiftMm: from the §7.2 tangent correction, read off the plotted curve.
  const unit = readings.map((r) => ({ pen: r.pen, kgcm2: (r.dial * ringFactor) / areaCm2 }));
  const at = (pen) => {
    const t = pen + originShiftMm;
    for (let i = 1; i < unit.length; i++)
      if (t <= unit[i].pen) {
        const [a, b] = [unit[i - 1], unit[i]];
        return a.kgcm2 + ((b.kgcm2 - a.kgcm2) * (t - a.pen)) / (b.pen - a.pen);
      }
    return unit[unit.length - 1].kgcm2;
  };
  const cbr25 = (at(2.5) / 70) * 100, cbr50 = (at(5) / 105) * 100; // 70 / 105 kg/cm² standard unit loads
  const issues = [];
  if (cbr50 > cbr25) issues.push(issue('warn', 'REPEAT', 'IS 2720-16:1987 7.3', 'CBR at 5 mm exceeds 2.5 mm — repeat test; adopt 5 mm only if identical'));
  return { result: { cbr25, cbr50, design: cbr50 > cbr25 ? cbr50 : cbr25 }, issues };
}

export function cbrSpecimen({ soakHours, expansion, replacedOversizeKg, compaction }) {
  const clause = 'IS 2720-16:1987';
  const issues = [];
  if (soakHours !== 96) issues.push(issue('warn', 'SOAK', `${clause} 5.1.1`, `Soaked ${soakHours} h (standard: 96 h)`));
  if (!expansion || ![expansion.dsMm, expansion.drMm, expansion.heightMm].every(num))
    issues.push(issue('block', 'EXPANSION', `${clause} 7.1 / 8.1`, 'Swell dial readings and specimen height required — result is CBR value and expansion ratio'));
  if (!num(replacedOversizeKg)) issues.push(issue('block', 'OVERSIZE', `${clause} 4.3.1 / App. A`, 'Oversize fraction replaced (kg) not recorded'));
  if (!['light', 'heavy'].includes(compaction)) issues.push(issue('block', 'COMPACTION', `${clause} App. A`, 'State light or heavy compaction'));
  const expansionRatio = expansion && [expansion.dsMm, expansion.drMm, expansion.heightMm].every(num)
    ? ((expansion.drMm - expansion.dsMm) / expansion.heightMm) * 100 : null;
  return { result: { expansionRatio }, issues };
}

// CBR at 95/97/100 % of MDD from the 3 moulds; flags extrapolation beyond tested density
export function cbrAtCompaction({ points, mddGcc, levels = [95, 97, 100] }) {
  const p = [...points].sort((a, b) => a.dry - b.dry);
  const issues = [];
  const result = levels.map((lv) => {
    const t = (mddGcc * lv) / 100;
    let i = p.findIndex((q) => q.dry >= t);
    const extrapolated = i === -1 || (i === 0 && p[0].dry > t);
    if (i === -1) i = p.length - 1;
    if (i === 0) i = 1;
    const [a, b] = [p[i - 1], p[i]];
    const cbr = a.cbr + ((b.cbr - a.cbr) * (t - a.dry)) / (b.dry - a.dry);
    if (extrapolated) issues.push(issue('warn', 'EXTRAPOLATED', 'IS 2720-16:1987', `${lv}% of MDD (${t.toFixed(3)} g/cc) is outside tested densities — value is extrapolated`));
    return { level: lv, dry: t, cbr, extrapolated };
  });
  return { result, issues };
}

// ---------- IS 2720 Part 29:1975 — core cutter field density ----------
export function coreCutter({ diameterCm, heightCm, volumeCc, passing475Pct, labMddGcc, dets }) {
  // dets = [{ cutterG, cutterSoilG, wetG, dryG, tareG }]
  const clause = 'IS 2720-29:1975';
  const issues = [];
  if (passing475Pct < 90) issues.push(issue('block', 'NOT_FINE_GRAINED', `${clause} 1.1.1`, `Only ${passing475Pct}% passes 4.75 mm (needs ≥ 90%) — use sand replacement (Part 28)`));
  if (dets.length < 3) issues.push(issue('block', 'DETS', `${clause} 3.5 Note`, `At least 3 determinations (have ${dets.length})`));
  const geo = (Math.PI / 4) * diameterCm ** 2 * heightCm;
  if (Math.abs(geo - volumeCc) / geo > 0.005) issues.push(issue('warn', 'VOLUME', `${clause} 3.1`, `Volume ${volumeCc} cc vs ${geo.toFixed(0)} cc from dimensions`));
  const rows = dets.map((d, i) => {
    issues.push(checkResolution(`Det ${i + 1} cutter+soil`, d.cutterSoilG, 1, `${clause} 3.4`));
    const bulk = (d.cutterSoilG - d.cutterG) / volumeCc;
    const w = moisture(d.wetG, d.dryG, d.tareG);
    return { bulk, w, dry: (100 * bulk) / (100 + w) };
  });
  const dryAvg = rows.reduce((s, r) => s + r.dry, 0) / rows.length;
  return {
    result: { rows, dryDensity: Number(dryAvg.toFixed(2)), wSig2: sig2(rows.reduce((s, r) => s + r.w, 0) / rows.length), compactionPct: (dryAvg / labMddGcc) * 100 },
    issues: collect(issues),
  };
}

// ---------- cross-test sanity ----------
export function crossCheck({ nmcPct, ll, compactionPct }) {
  const issues = [];
  if (num(nmcPct) && num(ll) && nmcPct > 1.5 * ll)
    issues.push(issue('warn', 'NMC_VS_LL', 'plausibility', `NMC ${nmcPct}% is far above LL ${ll}% — check weights`));
  if (num(compactionPct) && compactionPct > 100.5)
    issues.push(issue('warn', 'OVER_100', 'plausibility', `Compaction ${compactionPct.toFixed(1)}% exceeds lab MDD — confirm which Proctor record applies`));
  return issues;
}

export const hasBlocks = (issues) => issues.some((i) => i.level === 'block');
